<template>
  <div class="events">
    <div class="events__wrapper q-pa-md">
      <header class="events__header">
        <div class="events__title-wrap">
          <h2 class="text-h4 q-my-sm events__title">{{ $t(roleConfig.listingTitle) }}</h2>
        </div>

        <div class="header-actions" :class="{ 'header-actions--empty': !canAddEvent && this.user.role !== 'photographer' }">
          <q-btn
            v-if="this.user.role === 'photographer'"
            icon="arrow_left"
            color="secondary"
            :label="$t('events.goToLanding')"
            @click="goToLanding"
          />
          <q-btn
            v-if="canAddEvent"
            icon="add"
            color="primary"
            :label="$t('events.addEvent')"
            @click="openEventDialog"
          />
        </div>
      </header>
      <p class="text-subtitle1 q-mt-lg q-mb-sm events__description">
        {{ $t(roleConfig.listingDescription) }}
      </p>
      <p class="text-subtitle1 q-my-sm events__description" v-if="roleConfig.listingDescription2">
        {{ $t(roleConfig.listingDescription2) }}
      </p>
      <p class="text-subtitle1 q-mb-lg events__description" v-if="roleConfig.listingDescription3">
        {{ $t(roleConfig.listingDescription3) }}
      </p>

      <div v-if="this.user.role === 'sponsor'" class="global-sponsor-image">
        <p class="text-subtitle1 q-mt-md q-mb-sm">
          {{ $t('events.sponsorPhoto.description') }}
        </p>

        <q-form @submit.prevent="handleSetGlobalSponsorImage" class="global-sponsor-image-form row no-wrap-sm">
          <div class="col-xs-12 col-sm-4 global-sponsor-image__preview-wrapper">
            <div v-if="isGlobalSponsorImageFetching" class="global-sponsor-image__placeholder">
              <q-spinner color="primary" size="2em" />
            </div>

            <q-img
              v-else-if="globalSponsorImg"
              :src="globalSponsorImg"
              fit="contain"
              class="global-sponsor-image__preview"
            />

            <div v-else class="global-sponsor-image__placeholder">
              <q-icon name="image_not_supported" size="32px" color="grey-6" />
              <span>{{ $t('events.sponsorPhoto.noGlobalImage') }}</span>
            </div>
          </div>
          <q-card class="col-xs-12 col-sm-8 q-pa-md" bordered>
            <q-file
              v-model="globalSponsorImage"
              :label="$t('events.sponsorPhoto.globalImageField')"
              accept=".jpeg, .jpg, .png"
              max-file-size="5242880"
              :rules="[imageRequired]"
              hide-bottom-space
              @rejected="onFileRejected"
            >
              <template #append>
                <q-icon name="cloud_upload" />
              </template>
            </q-file>
            <div class="flex justify-end">
              <q-btn
                type="submit"
                color="primary"
                label="Zapisz"
                class="q-mt-lg"
                :loading="isGlobalSponsorPhotoLoading"
              />
            </div>
          </q-card>
        </q-form>
        
        <p class="text-subtitle1 q-mt-lg q-mb-sm global-sponsor-image-info">
          {{ $t('events.sponsorPhoto.info') }}
        </p>
      </div>

      <q-tabs v-model="currentTab" class="text-primary q-mb-md" outside-arrows>
        <q-tab name="verified" :label="$t('events.verifiedTab')" />
        <q-tab
          v-if="shouldShowUnverifiedTab"
          name="unverified"
          :label="$t(roleConfig.tabs.unverified)"
        />
      </q-tabs>

      <div v-if="loading" class="events-loading">
        <q-spinner class="q-mx-md q-mb-md" color="primary" size="3em" />
        <p>{{ $t('events.loading') }}</p>
      </div>

      <div
        v-if="!loading && currentEvents.length === 0"
        class="events-loading__error text-subtitle1"
      >
        {{ $t('events.noEventsMessage') }}
      </div>

      <div v-if="!loading && !currentError && currentEvents.length > 0">
        <EventCard
          v-for="event in currentEvents"
          :key="event.id"
          :event="event"
          :is-deleting="isDeleting"
          @go-to-dashboard="goToDashboard"
          @card-action="handleCardAction"
          @edit="editEvent(event)"
          @delete="handleUnverifiedEventDelete"
        />
      </div>

      <EventDialog
        v-model="isDialogOpen"
        :event="selectedEvent"
        :event-id="selectedEvent?.id"
        @close="handleDialogClose"
        @create-event="handleEventCreation"
        @update-event="handleEventUpdate"
      />

      <SponsorAssetsDialog
        v-if="selectedSponsorEvent"
        :is-open="isSponsorDialogOpen"
        :event="selectedSponsorEvent"
        @close="handleSponsorDialogClose"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { Notify } from 'quasar';
import EventDialog from '@/pages/dashboardEvents/dashboardEventsList/components/EventDialog.vue';
import EventCard from '@/pages/dashboardEvents/dashboardEventsList/components/EventCard.vue';
import SponsorAssetsDialog from '@/pages/dashboardEvents/dashboardEventsList/components/SponsorAssetsDialog.vue';
import userRoleMixin from '@/mixins/userRoleMixin.js';
import { handleFileRejected } from "@/helpers/fileHelper";

export default {
  name: 'DashboardEventsListPage',
  components: {
    EventDialog,
    EventCard,
    SponsorAssetsDialog,
  },
  mixins: [userRoleMixin],
  data() {
    return {
      globalSponsorImage: null,
      isDialogOpen: false,
      currentTab: 'verified',
      loading: true,
      errorVerified: null,
      errorUnverified: null,
      selectedEvent: null,
      selectedSponsorEvent: null,
      isDeleting: false,
      isSponsorDialogOpen: false,
      isGlobalSponsorPhotoLoading: false,
      isGlobalSponsorImageFetching: false
    };
  },
  computed: {
    ...mapState({
      verifiedEvents: (state) => state.dashboardEvents.events,
      unverifiedEvents: (state) => state.dashboardEvents.unverifiedEvents,
      user: (state) => state.auth.user,
      globalSponsorImg: (state) => state.dashboardEvents.globalSponsorImage
    }),
    currentEvents() {
      return this.currentTab === 'verified'
        ? this.verifiedEvents
        : this.unverifiedEvents;
    },
    currentError() {
      return this.currentTab === 'verified'
        ? this.errorVerified
        : this.errorUnverified;
    },
  },
  methods: {
    ...mapActions('dashboardEvents', [
      'fetchUserEvents',
      'fetchUserUnverifiedEvents',
      'updateEvent',
      'deleteEventVerification',
      'setGlobalSponsorImage',
      'fetchGlobalSponsorImage'
    ]),
    ...mapActions('global', ['getCities', 'getCountries']),
    async fetchVerified() {
      this.errorVerified = null;
      try {
        await this.fetchUserEvents();
      } catch (error) {
        this.errorVerified = error;
      }
    },
    editEvent(event) {
      this.selectedEvent = event;
      this.isDialogOpen = true;
    },
    goToLanding(){
      window.open(
        `${process.env.VUE_APP_BACK_ROOT}/fotograf/${this.user.user_nicename}`,
        '_blank'
      );
    },
    async fetchUnverified() {
      this.errorUnverified = null;
      try {
        await this.fetchUserUnverifiedEvents();
      } catch (error) {
        this.errorUnverified = error;
      }
    },
    openEventDialog() {
      this.isDialogOpen = true;
    },
    async handleEventCreation() {
      await this.fetchUnverified();
      this.currentTab = 'unverified';
    },
    async handleEventUpdate() {
      await this.fetchVerified();
      this.selectedEvent = null;
      this.currentTab = 'verified';
    },
    async handleUnverifiedEventDelete(eventId) {
      this.isDeleting = true;

      try {
        await this.deleteEventVerification(eventId);

        Notify.create({
          type: 'negative',
          message: this.$t('events.deleted'),
          position: 'bottom',
        });
      } catch (error) {
        Notify.create({
          type: 'negative',
          message: this.$t('events.deleteError'),
          position: 'bottom',
        });
      } finally {
        this.selectedEvent = null;
        this.currentTab = 'unverified';
        this.isDeleting = false;
      }
    },
    async handleSetGlobalSponsorImage() {
      this.isGlobalSponsorPhotoLoading = true;

      try {
        await this.setGlobalSponsorImage({
          sponsor_image: this.globalSponsorImage
        });
      } catch (error) {
        Notify.create({
          type: 'negative',
          message: this.$t('events.sponsorPhoto.formError'),
          position: 'bottom',
        });

        return this.isGlobalSponsorPhotoLoading = false;
      }

      Notify.create({
        type: 'positive',
        message: this.$t('events.sponsorPhoto.formSuccess'),
        position: 'bottom',
      });

      this.globalSponsorImage = this.globalSponsorImg;
      this.isGlobalSponsorPhotoLoading = false;
    },
    async loadGlobalSponsorImage() {
      this.isGlobalSponsorImageFetching = true;

      try {
        await this.fetchGlobalSponsorImage();
      } finally {
        this.isGlobalSponsorImageFetching = false;
      }
    },
    handleDialogClose() {
      this.selectedEvent = null;
      this.isDialogOpen = false;
    },
    handleSponsorDialogClose() {
      this.selectedSponsorEvent = null;
      this.isSponsorDialogOpen = false;
    },
    handleCardAction({ actionName, eventId }) {
      switch (actionName) {
        case 'goToDashboard':
          this.$router.push(`/my-events/${eventId}`);
          break;
        case 'uploadPhotos':
          this.$router.push(`/`);
          break;
        case 'goToAlbum':
          window.open(
            `${process.env.VUE_APP_BACK_ROOT}/album/${eventId}/1`,
            '_blank'
          );
          break;
        case 'manageAssets':
          this.selectedSponsorEvent = this.currentEvents.find(e => e.id === eventId);
          this.isSponsorDialogOpen = true;
          break;
        case 'manageSponsorImage':
          this.selectedEvent = this.currentEvents.find(e => e.id === eventId);
          this.isDialogOpen = true;
          break;
        default:
          console.error(`${actionName}`);
      }
    },
    onFileRejected(rejectedEntries) {
      handleFileRejected(rejectedEntries, this.$t);
    },
    imageRequired(file) {
      if (file || this.globalSponsorImg) {
        return true;
      }

      return this.$t('validation.required');
    },
  },
  async mounted() {
    this.loading = true;

    try {
      await Promise.all([this.getCities(), this.getCountries()]);
      await Promise.all([this.fetchVerified(), this.fetchUnverified(), this.loadGlobalSponsorImage()]);
    } catch (error) {
      Notify.create({
        type: 'negative',
        message: this.$t('error.fetchEvents'),
        position: 'bottom',
      });
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.events {
  background: #fff;
  width: 100%;
  min-height: calc(100vh - 50px);
  margin: 0 auto;
  display: flex;
  justify-content: center;
  padding-bottom: 50px;
  &-loading,
  &-loading__error {
    width: 300px;
    padding: 2rem 1rem 1rem 1rem;
    margin: 0 auto;
    text-align: center;
    &__error {
      border-bottom: 2px solid $primary;
    }
  }
  &__wrapper {
    width: 100%;
    max-width: 1000px;
    .events__header {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
    }

    .events__title-wrap {
      min-width: 0;
    }

    .events__title {
      line-height: 1.15;
      margin-bottom: 0;
      overflow-wrap: anywhere;
    }

    .events__description {
      margin-bottom: 8px;
    }
    .events__description:first-of-type {
      margin-top: 20px;
    }
    .events__description:last-of-type {
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px solid $accent-light;
    }

    .header-actions {
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
      width: 100%;

      &--empty {
        display: none;
      }

      :deep(.q-btn) {
        width: 100%;
        min-height: 44px;
      }
    }
  }

  .q-tab {
    min-height: 34px;
  }
}

.global-sponsor-image{
  &__preview-wrapper{
    min-height: 220px;
    display: flex;
    align-items: stretch;
  }

  &__preview{
    width: 100%;
    max-height: 220px;
    border-radius: 8px;
    background-color: #fff;
    overflow: hidden;
  }

  &__placeholder{
    width: 100%;
    min-height: 220px;
    border: 1px dashed $accent;
    border-radius: 8px;
    background: $accent-light;
    color: $dark;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
  }

  &-info{
    background-color: $accent-light;
    font-size: 14px;
    padding: 2px 4px;
    border-radius: 10px;
    text-align: center;
    font-weight: 600;
  }
}

@media screen and (min-width: $breakpoint-sm-min) {
  .no-wrap-sm{
    flex-wrap: nowrap;
  }

  .global-sponsor-image{
    &-form{
      gap: 16px;
      width: calc(100% - 16px);
    }
  }

  .events {
    &__wrapper {
      .events__header {
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
      }

      .events__title {
        margin-top: 2px;
      }

      .header-actions {
        width: auto;
        min-width: 220px;
        justify-items: end;

        :deep(.q-btn) {
          width: 100%;
          min-height: 38px;
        }
      }
    }
  }
}
</style>
